'use client'

import { Dialog, Transition } from '@headlessui/react'
import { FC, PropsWithChildren, useCallback, useEffect, useLayoutEffect } from 'react'

import styles from './Modal.module.scss'

interface IModalsProps {
  isOpen: boolean
  onClose: () => void
  transition?: boolean
  style?: React.CSSProperties
}

const Modal: FC<PropsWithChildren<IModalsProps>> = ({
  isOpen,
  onClose,
  children,
  transition,
  style,
}) => {
  useLayoutEffect(() => {
    const mobileResizeWindow = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    mobileResizeWindow()
    window.addEventListener('resize', mobileResizeWindow)

    return () => {
      window.removeEventListener('resize', mobileResizeWindow)
    }
  }, [isOpen])

  const onKeyBoardModalHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    document.addEventListener('keydown', onKeyBoardModalHandler)

    return () => {
      document.removeEventListener('keydown', onKeyBoardModalHandler)
    }
  }, [])

  return (
    <Transition
      show={isOpen}
      enter="ease-out duration-300"
      enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      enterTo="opacity-100 translate-y-0 sm:scale-100"
    >
      {!transition ? (
        <Dialog
          open={isOpen}
          onClose={onClose}
          className={`fixed inset-0 z-200 ${styles.ModalContainer}`}
        >
          <div
            className="flex h-full items-center justify-center"
            onMouseDown={e => e.stopPropagation()}
          >
            <Dialog.Overlay
              className={`absolute left-0 top-0 z-40 h-screen w-screen overflow-hidden ${styles.BgOpacity55} ${styles.heightfill}`}
            />
            <div
              className={`z-50 overflow-y-auto ${styles.ModalWrapper}`}
              style={style ? { ...style } : undefined}
            >
              {children}
            </div>
          </div>
        </Dialog>
      ) : (
        <Transition
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <Dialog
            open={isOpen}
            onClose={onClose}
            className={`fixed inset-0 z-200 ${styles.ModalContainer}`}
          >
            <div
              className="flex h-full items-center justify-center"
              onMouseDown={e => e.stopPropagation()}
            >
              <Dialog.Overlay
                className={`absolute left-0 top-0 z-40 h-screen w-screen overflow-hidden ${styles.BgOpacity55} ${styles.heightfill}`}
              />
              <div
                className={`z-50 overflow-y-auto ${styles.ModalWrapper}`}
                style={style ? { ...style } : undefined}
              >
                {children}
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </Transition>
  )
}

export default Modal
